// Computer players. A bot decides from its own seat's view (the same redacted
// view a person gets) plus its legal actions, so it can't cheat by
// construction and runs the same on the server as in hot-seat play.

import { SUITS, isPlayable, isTwo, pointsOf, strength, trumpOrder, type Card, type Suit } from './cards';
import { MOON_BID } from './rules';
import type { Action } from './types';
import type { GameView } from './view';

/** Rough points a single held card is worth to its holder, by rank in the named suit. */
const CARD_VALUE: Record<number, number> = {
  17: 1.9, // A: wins its trick and its own point
  16: 1.1, // K
  15: 0.8, // Q
  14: 1.3, // J (a point, decent rank)
  13: 1.1, // off-J
  12: 0.9, // Big Joker
  11: 0.8, // Little Joker
  10: 0.5, // 10: a point, but easily captured
  3: 1.0, // 3: three points if it survives
  2: 0.9, // off-3
  1: 1.0, // 2: always scores for whoever plays it
};

/**
 * Per-table-size correction, calibrated from all-bot games so a bot's bid is
 * made about 75–80% of the time. Tables with a partner take more than the hand
 * suggests (the partner leads the called card, usually the best one missing,
 * and points spread thin); 2-player hands face a full-strength opponent.
 */
const TABLE_ADJUST: Record<number, number> = { 2: -2.8, 3: 1.5, 4: 2.4, 5: 2.6, 6: 3.5, 7: 3.7, 8: 4.0 };

/**
 * Estimated points this hand could take in `suit`, counting a partner and the
 * kitty. Only the cards the bot would actually keep count: with 25 cards each
 * in a 2-player game, valuing every playable card made bots bid 12 and go set
 * every hand.
 */
export function handEstimate(
  cards: readonly Card[],
  suit: Suit,
  playerCount: number,
  hasPartner: boolean,
  handLimit = 6,
): number {
  let est = keptValue(cards, suit, handLimit);
  if (hasPartner) est += 2.2;
  return est + 0.6 + (TABLE_ADJUST[playerCount] ?? 0); // 0.6 for the kitty
}

/** What the cards a bot would keep in `suit` are worth on their own, with no partner or kitty. */
function keptValue(cards: readonly Card[], suit: Suit, handLimit: number): number {
  let value = 0;
  for (const c of bestKeep(cards, suit, handLimit, [])) value += CARD_VALUE[strength(c, suit)] ?? 0.15;
  return value;
}

/**
 * Below this, a hand doesn't bid however good the partner might be: with the
 * partner leading the called card, a blank hand still estimated as a make.
 */
const MIN_OWN_VALUE = 2.5;

function bestSuit(cards: readonly Card[], playerCount: number, hasPartner: boolean, handLimit: number): { suit: Suit; estimate: number } {
  let best = { suit: SUITS[0] as Suit, estimate: -Infinity };
  for (const suit of SUITS) {
    const estimate = handEstimate(cards, suit, playerCount, hasPartner, handLimit);
    if (estimate > best.estimate) best = { suit, estimate };
  }
  return best;
}

/**
 * Keep priority: rank, plus a strong bonus for points (keeping a 3 beats
 * keeping a 9). The 2 gets extra because its point is guaranteed to whoever
 * plays it, so it always beats a blank low card.
 */
function keepScore(card: Card, trump: Suit): number {
  return strength(card, trump) + pointsOf(card, trump) * 4 + (isTwo(card, trump) ? 6 : 0);
}

function bestKeep(cards: readonly Card[], trump: Suit, count: number, required: readonly Card[]): Card[] {
  const rest = cards.filter((c) => isPlayable(c, trump) && !required.includes(c));
  rest.sort((a, b) => keepScore(b, trump) - keepScore(a, trump));
  return [...required, ...rest].slice(0, count);
}

/** The most a hand will bid: what it's worth, or nothing if its own cards are blank. */
export function bidCeiling(own: number, estimate: number): number {
  return own >= MIN_OWN_VALUE ? Math.floor(estimate) : 0;
}

/**
 * A number in [0, 1) from what the seat can already see, so a bot can vary its
 * style without hidden information or an RNG: the same view always gives the
 * same choice (undo replays identically, and the no-cheating test holds).
 */
export function viewHash(cards: readonly Card[], handNumber: number, seat: number): number {
  let h = 2166136261 ^ (handNumber * 31 + seat);
  for (const c of cards.slice().sort().join('')) h = Math.imul(h ^ c.charCodeAt(0), 16777619);
  return ((h >>> 0) % 10007) / 10007;
}

/** Share of hands where a bot jumps straight to its ceiling instead of opening low. */
const JUMP_SHARE = 0.2;

/**
 * Picks from the legal raises (ascending, all at or under the ceiling). The
 * first bid of a hand sometimes jumps to the ceiling and otherwise opens low;
 * once outbid, a bot climbs a step at a time, or goes straight to its ceiling
 * when that's only a step away.
 */
export function chooseBid(raises: readonly number[], ceiling: number, myLast: number | null, roll: number): number {
  const lowest = raises[0]!;
  const highest = raises[raises.length - 1]!;
  if (myLast === null || myLast === 0) {
    if (roll < JUMP_SHARE) return highest;
    // Open at the minimum, or a step or two under the ceiling.
    if (roll < 0.7) return lowest;
    const under = ceiling - (roll < 0.85 ? 1 : 2);
    return raises.find((a) => a >= under) ?? lowest;
  }
  return highest - lowest <= 1 ? highest : lowest;
}

function partnersPossible(view: GameView): boolean {
  const n = view.config.playerCount;
  return n > 3 || (n === 3 && view.config.rules.partnerWithThree);
}

export function botAction(view: GameView, legal: readonly Action[]): Action | null {
  const seat = view.viewer;
  if (seat === null) return null;
  const phase = view.phase;
  const hand = view.hand;
  const mine = hand.hands[seat] ?? [];
  const n = view.config.playerCount;

  switch (phase.kind) {
    case 'bid': {
      if (phase.turn !== seat) return null;
      const amounts = [...new Set(legal.flatMap((a) => (a.type === 'bid' ? [a.amount] : [])))];
      const { suit, estimate } = bestSuit(mine, n, partnersPossible(view), view.config.rules.handLimit);
      const own = keptValue(mine, suit, view.config.rules.handLimit);
      const ceiling = bidCeiling(own, estimate);
      const raise = amounts.filter((a) => a > 0 && a <= ceiling && a < MOON_BID).sort((x, y) => x - y);
      if (raise.length === 0) {
        if (amounts.includes(0)) return { type: 'bid', amount: 0 };
        return { type: 'bid', amount: Math.min(...amounts), suit };
      }
      return { type: 'bid', amount: chooseBid(raise, ceiling, hand.bids[seat] ?? null, viewHash(mine, hand.number, seat)), suit };
    }

    case 'call': {
      if (hand.bidder !== seat) return null;
      // Call the strongest card you're missing: the best partner you can get.
      const order = trumpOrder(hand.trump!);
      return { type: 'call', card: order.find((c) => !mine.includes(c)) ?? order[0]! };
    }

    case 'pass': {
      if (hand.bidder !== seat) return null;
      if (hand.partner === null || hand.partner === 'unknown') return { type: 'give', cards: [] };
      const trump = hand.trump!;
      const kitty = (hand.kittyCards ?? []).filter((c) => isPlayable(c, trump) && c !== hand.called && mine.includes(c));
      const keep = bestKeep(mine, trump, view.config.rules.handLimit, hand.called && mine.includes(hand.called) ? [hand.called] : []);
      // Whatever the bidder wouldn't keep is better in the partner's hand than discarded.
      return { type: 'give', cards: kitty.filter((c) => !keep.includes(c)) };
    }

    case 'trim': {
      if (!phase.pending[seat]) return null;
      const trump = hand.trump!;
      const playable = mine.filter((c) => isPlayable(c, trump));
      const required = hand.called && playable.includes(hand.called) ? [hand.called] : [];
      const count = Math.min(view.config.rules.handLimit, playable.length);
      return { type: 'keep', cards: bestKeep(playable, trump, count, required) };
    }

    case 'play': {
      if (phase.turn !== seat) return null;
      const options = legal.flatMap((a) => (a.type === 'play' ? [a.card] : []));
      if (options.length <= 1) return options[0] ? { type: 'play', card: options[0] } : null;
      return { type: 'play', card: choosePlay(view, seat, options) };
    }

    default:
      return null;
  }
}

/** Which side `other` is on from `seat`'s point of view: true friend, false foe, null unknown. */
function friendOf(view: GameView, seat: number, other: number): boolean | null {
  if (other === seat) return true;
  const { bidder, partner } = view.hand;
  if (partner === 'unknown') return null;
  const side = (p: number) => p === bidder || p === partner;
  return side(seat) === side(other);
}

function choosePlay(view: GameView, seat: number, options: Card[]): Card {
  const hand = view.hand;
  const trump = hand.trump!;
  const trick = hand.trick!;
  const pts = (c: Card) => pointsOf(c, trump);
  const str = (c: Card) => strength(c, trump);
  const byStrength = options.slice().sort((a, b) => str(b) - str(a));
  const lowest = (cards: Card[]) => cards.slice().sort((a, b) => str(a) - str(b))[0]!;

  // Cards nobody has shown yet; anything here might still be played against us.
  const seen = new Set<Card>([...(hand.hands[seat] ?? []), ...hand.won.flat(), ...trick.plays.map((p) => p.card)]);
  const unseen = trumpOrder(trump).filter((c) => !seen.has(c));
  const topUnseen = unseen.length > 0 ? str(unseen[0]!) : 0;
  const isBoss = (c: Card) => str(c) > topUnseen;

  // A throwaway that costs nothing: the 2 scores for us whoever wins; otherwise the lowest no-point card.
  const two = options.find((c) => isTwo(c, trump));
  const blanks = options.filter((c) => pts(c) === 0);
  const throwaway = two ?? (blanks.length > 0 ? lowest(blanks) : lowest(options));

  if (trick.plays.length === 0) {
    // Leading: cash a sure winner, otherwise lead something cheap.
    const boss = byStrength.find(isBoss);
    if (boss) return boss;
    return blanks.length > 0 ? lowest(blanks) : throwaway;
  }

  const winning = trick.plays.reduce((best, p) => (str(p.card) > str(best.card) ? p : best));
  const friendWinning = friendOf(view, seat, winning.player) === true;
  const toCome = trick.order.length - trick.plays.length - 1;
  const trickPoints = trick.plays.reduce((n, p) => n + (isTwo(p.card, trump) ? 0 : pts(p.card)), 0);
  const winningIsSafe = toCome === 0 || str(winning.card) > topUnseen;

  if (friendWinning && winningIsSafe) {
    // Smear: hand our partner the points.
    const smear = options.filter((c) => pts(c) > 0 && !isTwo(c, trump)).sort((a, b) => pts(b) - pts(a))[0];
    return smear ?? throwaway;
  }

  const beaters = options.filter((c) => str(c) > str(winning.card));
  if (beaters.length > 0) {
    const sure = beaters.filter((c) => toCome === 0 || isBoss(c));
    // Take points (or protect our own point cards) with the cheapest card that holds.
    if (sure.length > 0 && (trickPoints > 0 || toCome === 0 || options.some((c) => pts(c) >= 3))) return lowest(sure);
    if (trickPoints >= 2 && toCome > 0) return byStrength[0]!;
  }
  return throwaway;
}
